import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { PARTICIPANT_GOAL } from "@/lib/constants";
import { type PipelineStage } from "@/lib/types";

interface ProgressChartProps {
  stageStats: Record<PipelineStage, number>;
}

export function ProgressChart({ stageStats }: ProgressChartProps) {
  const participantCount = stageStats.participant || 0;
  const committedCount = stageStats.committed || 0;
  
  // Calculate progress percentage
  const progressPercentage = Math.min((participantCount / PARTICIPANT_GOAL) * 100, 100);
  
  // Calculate potential progress including committed
  const potentialCount = participantCount + committedCount;
  const potentialPercentage = Math.min((potentialCount / PARTICIPANT_GOAL) * 100, 100);
  
  // Calculate remaining needed
  const remaining = Math.max(PARTICIPANT_GOAL - participantCount, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Progress to Goal</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Participants</span>
            <span className="font-mono">{participantCount} / {PARTICIPANT_GOAL}</span>
          </div>
          <Progress value={progressPercentage} className="w-full h-3" />
          <p className="text-xs text-muted-foreground">
            {Math.round(progressPercentage)}% complete
          </p>
        </div>

        {/* Potential progress (including committed) */}
        {committedCount > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Potential (+ Committed)</span>
              <span className="font-mono">{potentialCount} / {PARTICIPANT_GOAL}</span>
            </div>
            <Progress value={potentialPercentage} className="w-full h-2 opacity-60" />
            <p className="text-xs text-muted-foreground">
              {Math.round(potentialPercentage)}% if all committed convert
            </p>
          </div>
        )}

        {/* Goal status */}
        <div className="pt-4 border-t">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">{participantCount}</div>
              <div className="text-xs text-muted-foreground">Achieved</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-600">{remaining}</div>
              <div className="text-xs text-muted-foreground">Remaining</div>
            </div>
          </div>
        </div>

        {/* Milestone indicators */}
        <div className="space-y-1">
          {[25, 50, 75, 100].map(milestone => {
            const reached = participantCount >= milestone;
            const upcoming = !reached && participantCount >= milestone - 10;
            
            return (
              <div key={milestone} className="flex items-center gap-2 text-xs">
                <div className={`w-2 h-2 rounded-full ${
                  reached ? 'bg-green-500' : upcoming ? 'bg-yellow-500' : 'bg-slate-300'
                }`} />
                <span className={reached ? 'text-green-600' : 'text-muted-foreground'}>
                  {milestone} participants {reached ? '✓' : ''}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}